import { pick, uniq } from 'lodash';
import {
  BaseChangeItem,
  BaseNodeItemChange,
  BaseNodeItemChangeNode,
  ChangesetItemType,
  ChangesetReason,
  Design,
  DesignChangeMap,
  DesignMap,
  KeysByExactValue,
  ModifyChangeState
} from '@app/types';
import { createTimeLogger } from '@app/utilities';
import { ChangeSets } from '../ChangeSets';
import { MapTypes } from '../MapTypes';

/** Base values that are the same on all changes. */
type BaseChange = {
  author: string;
  version: string;
};

/** Item that we know how to get a timestamp off from. */
type ItemWithChangeTimestamp = (
  { props: { lastLocalChange?: number; }; }
  | { lastLocalChange: number; }
);

/** Creates a change for an item stored on the `object` property. */
function createObjectItemChange<TItem extends { id: string; } | { config: string; }>({
  currentItem,
  id,
  previousItem,
  ...rest
}: ListChangeFactoryProps<TItem>): Record<string, BaseChangeItem & { object: TItem; }> {
  const object = currentItem ?? previousItem!;
  return { [id]: { ...rest, object } };
};

/** Creates a change for an item stored on the `node` property. */
function createNodeItemChange<TItem extends BaseNodeItemChangeNode>({
  currentItem,
  id,
  previousItem,
  ...rest
}: ListChangeFactoryProps<TItem>): Record<string, BaseNodeItemChange<TItem>> {
  const node = currentItem ?? previousItem!;
  return {
    [id]: {
      ...rest,
      node,
      prevConfig: (rest.reason === ChangesetReason.modified) ? (
        previousItem?.props.config
      ) : undefined
    }
  };
};

/** When a change is found, these properties are sent to the change creator. */
type ListChangeFactoryProps<TItem> = (
  BaseChange
  & { id: string; }
  & (
    {
      currentItem: TItem;
      previousItem?: never;
      reason: ChangesetReason.added
    } | {
      currentItem?: never;
      previousItem: TItem;
      reason: ChangesetReason.deleted
    } | (
      ModifyChangeState
      & {
        currentItem: TItem;
        previousItem: TItem;
      }
    )
  )
);

type SetListChangesProps<
  KChange extends keyof(DesignChangeMap),
  TItem extends ItemWithChangeTimestamp
> = {
  /** Basic properties shared by all changes. */
  baseChange: BaseChange;
  /** Key on the @see DesignChangeMap which is also used to compare @see TItem for modifications. */
  changeKey: KChange;
  /** Changes so far. */
  changes: DesignChangeMap;
  /** Factory to generate changes betwee two @see TItem objects. */
  changesets: ChangeSets;
  /** Create a new change item to place on @see changes. */
  createChange: (props: ListChangeFactoryProps<TItem>) => DesignChangeMap[KChange];
  currentMap: Map<string, TItem> | undefined;
  previousMap: Map<string, TItem> | undefined;
};

/** Checks for changes on between two arrays (stored in Maps). */
function setListChanges<
  KChange extends keyof(DesignChangeMap),
  TItem extends ItemWithChangeTimestamp
>({
  baseChange,
  changes,
  changesets,
  currentMap,
  changeKey: mapType,
  createChange,
  previousMap
}: SetListChangesProps<KChange, TItem>): void {
  const ids = uniq([
    ...(currentMap?.keys() ?? []),
    ...(previousMap?.keys() ?? [])
  ]);

  ids.forEach((id) => {
    let currentItem = currentMap?.get(id);
    let previousItem = previousMap?.get(id);

    // Extra check needed when upgrading app properties to have IDs.
    const object = currentItem ?? previousItem!;
    const backupId = (
      object
      && 'config' in object
      && typeof object.config === 'string'
    ) ? object.config : null;
    previousItem = (!previousItem && backupId) ? (
      previousMap?.get(backupId)
    ) : previousItem;
    currentItem = (!currentItem && backupId) ? (
      currentMap?.get(backupId)
    ) : currentItem;

    // Skip using a backupId when both items have primary IDs.
    if (
      id === backupId
      && ((previousItem) ? 'id' in previousItem : 'id' in currentItem!)
    ) {
      return;
    }

    if (!previousItem) {
      if (!currentItem) {
        throw new Error(`Unable to find ${mapType} with ID '${id}'.`);
      }

      changes[mapType] = {
        ...changes[mapType],
        ...createChange({
          ...baseChange,
          id,
          currentItem,
          reason: ChangesetReason.added
        })
      };
      return;
    } else if (!currentItem) {
      changes[mapType] = {
        ...changes[mapType],
        ...createChange({
          ...baseChange,
          id,
          previousItem,
          reason: ChangesetReason.deleted
        })
      };
      return;
    }

    const currentLastChange = 'lastLocalChange' in currentItem ? currentItem.lastLocalChange : (
      currentItem.props.lastLocalChange
    );
    const previousLastChange = 'lastLocalChange' in previousItem ? previousItem.lastLocalChange : (
      previousItem.props.lastLocalChange
    );

    if (currentLastChange !== previousLastChange) {
      const changeset = changesets.getChangeSet(previousItem, currentItem, mapType);
      if (changeset.length) {
        changes[mapType] = {
          ...changes[mapType],
          ...createChange({
            ...baseChange,
            changeset,
            currentItem,
            id: ('id' in previousItem || !backupId) ? id : backupId,
            previousItem,
            reason: ChangesetReason.modified
          })
        };
      }
    }
  });
}

type SettingsChangeFactoryProps = (
  BaseChange
  & {
    changeset: [{ type: ChangesetItemType.overwrite }];
    lastChange: number;
    reason: ChangesetReason.modified;
  }
);

type SetSettingsChangesProps<
  KChange extends keyof(DesignChangeMap)
> = {
  /** Basic properties shared by all changes. */
  baseChange: BaseChange;
  /** Key on the @see DesignChangeMap. */
  changeKey: KChange;
  /** Changes so far. */
  changes: DesignChangeMap;
  /** Design with changes. */
  current: Design;
  /** Create a new change item to place on @see changes. */
  createChange: (props: SettingsChangeFactoryProps) => DesignChangeMap[KChange];
  /** Design before changes. */
  previous: Design;
  /** Gets a timestamp to see if a change happened. */
  timeGetter: (
    KeysByExactValue<Design, number | undefined>
    | ((design: Design) => number | undefined)
  )
};

/** Checks for changes to legacy settings properties. */
function setSettingsChanges<
  KChange extends keyof(DesignChangeMap)
>({
  baseChange,
  changeKey,
  changes,
  createChange,
  current,
  previous,
  timeGetter
}: SetSettingsChangesProps<KChange>): void {
  const [
    currentTimestamp,
    previousTimestamp
  ] = (typeof timeGetter === 'string') ? (
    [current[timeGetter], previous[timeGetter]]
  ) : (
    [timeGetter(current), timeGetter(previous)]
  );

  if (currentTimestamp && currentTimestamp !== previousTimestamp) {
    changes[changeKey] = createChange({
      ...baseChange,
      changeset: [{ type: ChangesetItemType.overwrite }],
      lastChange: currentTimestamp,
      reason: ChangesetReason.modified
    });
  }
}

type SetIntentHandlingChangeProps = (
  Pick<
    SetSettingsChangesProps<MapTypes.INTENT_HANDLING>,
    (
      'baseChange'
      | 'changes'
      | 'current'
      | 'previous'
    )
  >
  & {
  /** Factory to generate changes betwee two @see TItem objects. */
    changesets: ChangeSets;
  }
);

/** Checks for changes to intent handling. */
function setIntentHandlingChange({
  baseChange,
  changes,
  changesets,
  current,
  previous
}: SetIntentHandlingChangeProps): void {
  const { intentHandling: currentHandling } = current;
  const { intentHandling: previousHandling } = previous;
  const currentIsUpdated = (
    currentHandling
    && !('inARowExcluded' in currentHandling)
    && !('xAndOutExcluded' in currentHandling)
  );
  const previousIsUpdated = (
    previousHandling
    && !('inARowExcluded' in previousHandling)
    && !('xAndOutExcluded' in previousHandling)
  );

  if (!currentHandling) {
    // Ignore missing and outdated currentHandling.
    return;
  } else if (!previousHandling) {
    changes.intentHandling = {
      ...baseChange,
      object: currentHandling,
      reason: ChangesetReason.added
    };
    return;
  } else if (!currentIsUpdated || !previousIsUpdated) {
    // If either is outdated, then do a standard diff.
    const changeset = changesets.getChangeSet(
      previous,
      current,
      MapTypes.INTENT_HANDLING
    );
    if (changeset.length) {
      changes.intentHandling = {
        ...baseChange,
        changeset,
        object: currentHandling,
        reason: ChangesetReason.modified
      };
    }
    return;
  }

  if (currentHandling.inARowLimitLastLocalChange !== previousHandling.inARowLimitLastLocalChange) {
    changes.settings ??= {};
    changes.settings.intentHandling ??= {};
    changes.settings.intentHandling.inARowLimit = {
      ...baseChange,
      changeset: [{ type: ChangesetItemType.overwrite }],
      object: currentHandling.inARowLimit,
      lastChange: currentHandling.inARowLimitLastLocalChange,
      reason: ChangesetReason.modified
    };
  }

  if (currentHandling.xAndOutLimitLastLocalChange > previousHandling.xAndOutLimitLastLocalChange) {
    changes.settings ??= {};
    changes.settings.intentHandling ??= {};
    changes.settings.intentHandling.xAndOutLimit = {
      ...baseChange,
      changeset: [{ type: ChangesetItemType.overwrite }],
      object: currentHandling.xAndOutLimit,
      lastChange: currentHandling.xAndOutLimitLastLocalChange,
      reason: ChangesetReason.modified
    };
  }
}

/** Get the changes that happened on @see current since @see previous. */
export function createDesignChangeMap(
  current: Design,
  previous: Design
): DesignChangeMap {
  if (current === previous) {
    return {};
  }

  const logTime = createTimeLogger('createDesignChangeMap');
  const currentMap = new DesignMap(current);
  const previousMap = new DesignMap(previous);
  const changes: DesignChangeMap = {};
  const changesets = new ChangeSets();
  const props = {
    baseChange: {
      author: current.author,
      version: current.version
    },
    changes,
    changesets
  };

  [...currentMap.modules.keys()].forEach((moduleId) => {
    if (!previousMap.modules.has(moduleId)) {
      return;
    }

    setListChanges({
      ...props,
      createChange: ({
        currentItem,
        id: _,
        previousItem,
        ...rest
      }) => {
        const link = currentItem ?? previousItem;
        changes[MapTypes.MODULES] ??= {};
        changes[MapTypes.MODULES][moduleId] ??= {};
        changes[MapTypes.MODULES][moduleId].links ??= [];
        changes[MapTypes.MODULES][moduleId].links.push({ ...rest, link });
        return changes[MapTypes.MODULES];
      },
      currentMap: currentMap.moduleLinks.get(moduleId),
      changeKey: MapTypes.MODULES,
      previousMap: previousMap.moduleLinks.get(moduleId)
    });

    setListChanges({
      ...props,
      createChange: ({
        currentItem,
        id: _,
        previousItem,
        ...rest
      }) => {
        const object = currentItem ?? previousItem;
        changes[MapTypes.MODULES] ??= {};
        changes[MapTypes.MODULES][moduleId] ??= {};
        changes[MapTypes.MODULES][moduleId].localVariables ??= [];
        changes[MapTypes.MODULES][moduleId].localVariables.push({ ...rest, object });
        return changes[MapTypes.MODULES];
      },
      currentMap: currentMap.moduleLocalVariables.get(moduleId),
      changeKey: MapTypes.MODULES,
      previousMap: previousMap.moduleLocalVariables.get(moduleId)
    });

    setListChanges({
      ...props,
      createChange: ({
        currentItem,
        id: _,
        previousItem,
        ...rest
      }) => {
        const node = currentItem ?? previousItem;
        changes[MapTypes.MODULES] ??= {};
        changes[MapTypes.MODULES][moduleId] ??= {};
        changes[MapTypes.MODULES][moduleId].nodes ??= [];
        changes[MapTypes.MODULES][moduleId].nodes.push({
          ...rest,
          node,
          prevConfig: (rest.reason === ChangesetReason.modified) ? (
            previousItem?.props.config
          ) : undefined
        });
        return changes[MapTypes.MODULES];
      },
      currentMap: currentMap.moduleNodes.get(moduleId),
      changeKey: MapTypes.MODULES,
      previousMap: previousMap.moduleNodes.get(moduleId)
    });
  });

  setListChanges({
    ...props,
    createChange: createNodeItemChange,
    currentMap: currentMap.intents,
    changeKey: MapTypes.APPLICATION_INTENTS,
    previousMap: previousMap.intents
  });

  setListChanges({
    ...props,
    createChange: createNodeItemChange,
    currentMap: currentMap.modules,
    changeKey: MapTypes.APPLICATION_MODULES,
    previousMap: previousMap.modules
  });

  setListChanges({
    ...props,
    createChange: createNodeItemChange,
    currentMap: currentMap.objects,
    changeKey: MapTypes.APPLICATION_OBJECTS,
    previousMap: previousMap.objects
  });

  setListChanges({
    ...props,
    createChange: createNodeItemChange,
    currentMap: currentMap.variables,
    changeKey: MapTypes.APPLICATION_VARIABLES,
    previousMap: previousMap.variables
  });

  setListChanges({
    ...props,
    createChange: createObjectItemChange,
    currentMap: currentMap.folderModules,
    changeKey: MapTypes.FOLDERS,
    previousMap: previousMap.folderModules
  });

  setListChanges({
    ...props,
    createChange: createObjectItemChange,
    currentMap: currentMap.functions,
    changeKey: MapTypes.FUNCTIONS,
    previousMap: previousMap.functions
  });

  setListChanges({
    ...props,
    createChange: createObjectItemChange,
    currentMap: currentMap.globalIntents,
    changeKey: MapTypes.GLOBAL_INTENTS,
    previousMap: previousMap.globalIntents
  });

  setListChanges({
    ...props,
    createChange: createObjectItemChange,
    currentMap: currentMap.compoundIntents,
    changeKey: MapTypes.COMPOUND_INTENTS,
    previousMap: previousMap.compoundIntents
  });

  [...currentMap.fillerPrompts.keys()].forEach((fillerType) => {
    const currentFillers = currentMap.fillerPrompts.get(fillerType);
    const previousFillers = previousMap.fillerPrompts.get(fillerType);

    setListChanges({
      ...props,
      createChange: ({
        currentItem,
        id,
        previousItem,
        ...rest
      }) => {
        const object = currentItem ?? previousItem;
        changes[MapTypes.FILLER_PROMPTS] ??= {};
        changes[MapTypes.FILLER_PROMPTS][fillerType] ??= {};
        changes[MapTypes.FILLER_PROMPTS][fillerType][id] ??= {
          ...rest,
          object
        };
        return changes[MapTypes.FILLER_PROMPTS];
      },
      currentMap: currentFillers,
      changeKey: MapTypes.FILLER_PROMPTS,
      previousMap: previousFillers
    });
  });

  setListChanges({
    ...props,
    createChange: createObjectItemChange,
    currentMap: currentMap.graphQlSchemas,
    changeKey: MapTypes.GRAPHQL_SCHEMAS,
    previousMap: previousMap.graphQlSchemas
  });

  setListChanges({
    ...props,
    createChange: createObjectItemChange,
    currentMap: currentMap.csvs,
    changeKey: MapTypes.CSV_DATA,
    previousMap: previousMap.csvs
  });

  const modifyOnlyProps = {
    ...props,
    current,
    previous
  };

  setSettingsChanges({
    ...modifyOnlyProps,
    changeKey: MapTypes.LANGUAGES,
    createChange: (change) => {
      return {
        ...change,
        object: {
          languagesMeta: current.languagesMeta!,
          ...pick(current, [
            'language',
            'languages',
            'voice'
          ])
        }
      };
    },
    timeGetter: (design) => {
      return design.languagesMeta?.lastLocalChange;
    }
  });

  setSettingsChanges({
    ...modifyOnlyProps,
    changeKey: MapTypes.CCXML,
    createChange: (change) => {
      return {
        default: {
          ...change,
          object: current.ccxml
        }
      };
    },
    timeGetter: 'ccxmlLastLocalChange'
  });

  setSettingsChanges({
    ...modifyOnlyProps,
    changeKey: MapTypes.SETTINGS,
    createChange: ({ lastChange: _, ...change }) => {
      return {
        ...changes.settings,
        globalErrorHandling: {
          ...change,
          changeset: changesets.getChangeSet(
            previous.globalErrorHandling,
            current.globalErrorHandling,
            MapTypes.GLOBAL_ERROR_HANDLING
          ),
          object: current.globalErrorHandling
        }
      };
    },
    timeGetter: (design) => {
      return design.globalErrorHandling.lastLocalChange;
    }
  });

  setSettingsChanges({
    ...modifyOnlyProps,
    changeKey: MapTypes.SETTINGS,
    createChange: ({ lastChange: _, ...change }) => {
      return {
        ...changes.settings,
        globalExceptionHandling: {
          ...change,
          changeset: changesets.getChangeSet(
            previous.globalExceptionHandling,
            current.globalExceptionHandling,
            MapTypes.GLOBAL_ERROR_HANDLING
          ),
          object: current.globalExceptionHandling
        }
      };
    },
    timeGetter: (design) => {
      return design.globalExceptionHandling.lastLocalChange;
    }
  });

  setSettingsChanges({
    ...modifyOnlyProps,
    changeKey: MapTypes.SETTINGS,
    createChange: ({ lastChange: _, ...change }) => {
      const currentInARow = current.globalLoopHandling?.inARow;
      const previousInARow = previous.globalLoopHandling?.inARow;
      const currentXAndOut = current.globalLoopHandling?.xAndOut;
      const previousXAndOut = previous.globalLoopHandling?.xAndOut;

      if (currentInARow !== previousInARow) {
        changes.settings ??= {};
        changes.settings.globalLoopHandling ??= {};
        changes.settings.globalLoopHandling.inARow = {
          ...change,
          object: current.globalLoopHandling.inARow
        };
      }

      if (currentXAndOut !== previousXAndOut) {
        changes.settings ??= {};
        changes.settings.globalLoopHandling ??= {};
        changes.settings.globalLoopHandling.xAndOut = {
          ...change,
          object: current.globalLoopHandling.xAndOut
        };
      }

      return changes.settings;
    },
    timeGetter: (design) => {
      return design.globalLoopHandling.lastLocalChange;
    }
  });

  setIntentHandlingChange({
    ...props,
    current,
    previous
  });

  setSettingsChanges({
    ...modifyOnlyProps,
    changeKey: MapTypes.SETTINGS,
    createChange: (change) => {
      return {
        ...changes.settings,
        description: {
          ...change,
          object: current.description
        }
      };
    },
    timeGetter: 'descriptionLastLocalChange'
  });

  setSettingsChanges({
    ...modifyOnlyProps,
    changeKey: MapTypes.SETTINGS,
    createChange: (change) => {
      return {
        ...changes.settings,
        noInputTimeout: {
          ...change,
          object: current.noInputTimeout
        }
      };
    },
    timeGetter: 'noInputTimeoutLastLocalChange'
  });

  setSettingsChanges({
    ...modifyOnlyProps,
    changeKey: MapTypes.SETTINGS,
    createChange: (change) => {
      return {
        ...changes.settings,
        dtmf_live_agent_override_pattern: {
          ...change,
          object: current.dtmf_live_agent_override_pattern
        }
      };
    },
    timeGetter: 'dtmf_live_agent_override_patternLastLocalChange'
  });

  setSettingsChanges({
    ...modifyOnlyProps,
    changeKey: MapTypes.SETTINGS,
    createChange: ({ lastChange: _, ...change }) => {
      return {
        ...changes.settings,
        rootSettings: {
          ...change,
          changeset: changesets.getChangeSet(
            previous.rootSettings ?? {},
            current.rootSettings,
            null
          ),
          object: current.rootSettings
        }
      };
    },
    timeGetter: (design) => {
      return design.rootSettings?.lastLocalChange;
    }
  });

  setListChanges({
    ...props,
    createChange: createObjectItemChange,
    currentMap: currentMap.appProperties,
    changeKey: MapTypes.APP_PROPERTIES,
    previousMap: previousMap.appProperties
  });

  setListChanges({
    ...props,
    createChange: createObjectItemChange,
    currentMap: currentMap.unitTests,
    changeKey: MapTypes.UNIT_TESTS,
    previousMap: previousMap.unitTests
  });

  setSettingsChanges({
    ...modifyOnlyProps,
    changeKey: MapTypes.SETTINGS,
    createChange: ({ lastChange: _, ...change }) => {
      return {
        ...changes.settings,
        omniChannel: {
          ...change,
          changeset: changesets.getChangeSet(
            previous.omniChannel,
            current.omniChannel,
            MapTypes.OMNICHANNEL
          ),
          object: current.omniChannel
        }
      };
    },
    timeGetter: (design) => {
      return design.omniChannel.lastLocalChange;
    }
  });

  setSettingsChanges({
    ...modifyOnlyProps,
    changeKey: MapTypes.APPLICATION_VERSION,
    createChange: (change) => {
      return {
        ...change,
        object: current.applicationVersion
      };
    },
    timeGetter: 'applicationVersionLastChange'
  });

  setSettingsChanges({
    ...modifyOnlyProps,
    changeKey: MapTypes.SETTINGS,
    createChange: ({ lastChange: _, ...change }) => {
      return {
        ...changes.settings,
        sensitiveOverrides: {
          ...change,
          changeset: changesets.getChangeSet(
            previous.sensitiveOverrides ?? {},
            current.sensitiveOverrides!,
            null
          ),
          object: current.sensitiveOverrides
        }
      };
    },
    timeGetter: (design) => {
      return design.sensitiveOverrides?.lastLocalChange;
    }
  });

  logTime('end');
  return changes;
}
